import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  AvatarFallback,
  Button,
  CollapsibleContainer,
  FormInputField,
  FormSelectField,
  FormTextareaField,
  Heading,
  Paragraph,
} from '@centrix/shared-ui';
import {
  ApiError,
  useCompanies,
  useCurrentUser,
  useOnboardingStatus,
  useUpdateCompany,
} from '@centrix/api-client';
import { getMfeAppUrl, isAdminUser } from '@centrix/types';
import type { AuthUser } from '@centrix/api-client';

import logoImage from '@/assets/logo.png';

import { validateOrgName } from '@/utils/authValidation';
import { buildOnboardingPath, resolveOnboardingToken } from './onboarding';

const FORM_LABEL_CLASS_NAME = 'mb-1 text-xs text-gray-placeholder';
const FORM_INPUT_CLASS_NAME =
  'h-11 rounded px-4 py-2 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 border-gray-placeholder';
const FORM_TEXTAREA_CLASS_NAME =
  'rounded px-4 py-2 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 border-gray-placeholder resize-none';
const FORM_SELECT_TRIGGER_CLASS_NAME =
  'h-11 rounded px-4 py-2 text-sm text-gray-placeholder data-[placeholder]:text-gray-placeholder border-gray-placeholder';

const ROLE_OPTIONS = [
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Administrator' },
  { value: 'other', label: 'Other' },
];

const TYPE_OF_PROVISION_OPTIONS = [
  { value: 'residential', label: 'Residential Care' },
  { value: 'fostering', label: 'Fostering' },
  { value: 'supported_accommodation', label: 'Supported Accommodation' },
  { value: 'day_services', label: 'Day Services' },
];

const LOCAL_AUTHORITY_OPTIONS = [
  { value: 'london', label: 'London' },
  { value: 'manchester', label: 'Manchester' },
  { value: 'birmingham', label: 'Birmingham' },
  { value: 'other', label: 'Other' },
];

const COUNTRY_OPTIONS = [
  { value: 'uk', label: 'United Kingdom' },
  { value: 'ie', label: 'Ireland' },
];

const REGION_OPTIONS = [
  { value: 'north_west', label: 'North West' },
  { value: 'south_east', label: 'South East' },
  { value: 'midlands', label: 'Midlands' },
  { value: 'other', label: 'Other' },
];

const MISSING_ONBOARDING_EMAIL =
  'We could not determine the onboarding email for this profile. Please restart the signup flow.';
const MISSING_COMPANY_CONTEXT =
  'Unable to load your company profile. Please refresh the page and try again.';

// Post-onboarding dashboards. When the user reaches this screen after logging
// in (no onboarding token), saving the company should drop them into the app.
const ADMIN_DASHBOARD_URL = getMfeAppUrl('admin', {
  env: import.meta.env,
  isDev: import.meta.env.DEV,
});
const LEARNER_DASHBOARD_URL = getMfeAppUrl('learning', {
  env: import.meta.env,
  isDev: import.meta.env.DEV,
});

function getDashboardUrl(user: AuthUser | null | undefined): string {
  return user && isAdminUser(user) ? ADMIN_DASHBOARD_URL : LEARNER_DASHBOARD_URL;
}

function resolveEnterpriseUuid(user: AuthUser | null | undefined): string | undefined {
  return user?.roles?.find((role) => Boolean(role.enterprise_uuid))?.enterprise_uuid;
}

export const CompleteProfileStep1Page: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const token = resolveOnboardingToken({ locationState: location.state, searchParams });
  const hasOnboardingToken = Boolean(token);
  const { data: onboardingStatus, isLoading: isOnboardingLoading } = useOnboardingStatus(
    token || undefined
  );

  // When the user lands here after logging in (no onboarding token), resolve
  // their company from the authenticated session instead of the onboarding
  // state, so the form can still save and route them into the dashboard.
  const { data: currentUser } = useCurrentUser();
  const sessionEnterpriseUuid = resolveEnterpriseUuid(currentUser);
  const shouldLoadSessionCompany = !hasOnboardingToken && Boolean(sessionEnterpriseUuid);
  const { data: sessionCompanies, isLoading: isSessionCompaniesLoading } = useCompanies(
    { page_size: 100 },
    { enabled: shouldLoadSessionCompany }
  );
  const sessionCompany = shouldLoadSessionCompany
    ? sessionCompanies?.results.find((company) => company.enterprise_uuid === sessionEnterpriseUuid)
    : undefined;

  const email =
    onboardingStatus?.email ?? currentUser?.email ?? searchParams.get('email')?.trim() ?? '';
  const companySlug = onboardingStatus?.company_slug ?? sessionCompany?.slug ?? undefined;
  const isCompaniesLoading =
    isOnboardingLoading || (shouldLoadSessionCompany && isSessionCompaniesLoading);

  const { mutateAsync: updateCompany } = useUpdateCompany(token || null);

  const [orgName, setOrgName] = useState('');
  // TODO: adminName is captured in the UI but not sent to the API yet.
  // The Company model lacks an admin_name field. Wire up once the backend adds it.
  const [adminName, setAdminName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleInCompany, setRoleInCompany] = useState('');
  const [companyReg, setCompanyReg] = useState('');
  const [ofstedNumber, setOfstedNumber] = useState('');
  const [ofstedLink, setOfstedLink] = useState('');
  const [typeOfProvision, setTypeOfProvision] = useState('');
  const [localAuthority, setLocalAuthority] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [statementOfPurpose, setStatementOfPurpose] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    orgName?: string;
    phone?: string;
    ofstedLink?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isFormDisabled = isLoading || isCompaniesLoading;

  useEffect(() => {
    const nameFromOnboarding = (
      onboardingStatus?.data as { organisation_name?: string } | undefined
    )?.organisation_name;
    if (nameFromOnboarding && !orgName) {
      setOrgName(nameFromOnboarding);
    }
  }, [onboardingStatus?.data, orgName]);

  useEffect(() => {
    if (sessionCompany?.name && !orgName) {
      setOrgName(sessionCompany.name);
    }
  }, [sessionCompany?.name, orgName]);

  // The error alert sits at the top of a long form; scroll it into view so the
  // user never perceives the submit button as dead when validation/save fails.
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  // TODO: logoFile is captured and previewed but not sent to the API.
  // The Company model has a `logo` string field but the backend does not yet
  // support multipart file upload on the PATCH endpoint. Wire this up once the
  // backend adds a file-upload / presigned-URL flow for company logos.
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError(MISSING_ONBOARDING_EMAIL);
      return;
    }

    if (!companySlug) {
      setError(MISSING_COMPANY_CONTEXT);
      return;
    }

    const errors: { orgName?: string; phone?: string; ofstedLink?: string } = {};

    const orgNameError = validateOrgName(orgName);
    if (orgNameError) errors.orgName = orgNameError;

    if (phone && !/^[+\d][\d\s\-().]{6,}$/.test(phone)) {
      errors.phone = 'Please enter a valid phone number.';
    }

    if (ofstedLink && !/^https?:\/\/.+/.test(ofstedLink.trim())) {
      errors.ofstedLink = 'Please enter a valid URL (starting with http:// or https://).';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    try {
      await updateCompany({
        id: companySlug,
        data: {
          name: orgName || undefined,
          contact_email: email || undefined,
          phone_number: phone || undefined,
          company_registration: companyReg || undefined,
          ofsted_register_number: ofstedNumber || undefined,
          ofsted_report_link: ofstedLink || undefined,
          type_of_provision: typeOfProvision || undefined,
          local_authority: localAuthority || undefined,
          country: country || undefined,
          region: region || undefined,
          statement_of_purpose: statementOfPurpose || undefined,
          additional_information: additionalInfo || undefined,
        },
      });

      if (hasOnboardingToken) {
        navigate(buildOnboardingPath('/signup/complete-profile/learners', { email }), {
          state: { email },
        });
      } else {
        window.location.href = getDashboardUrl(currentUser);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to save profile. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    if (hasOnboardingToken) {
      navigate(buildOnboardingPath('/signup/complete-profile/learners', { email }), {
        state: { email },
      });
      return;
    }
    window.location.href = getDashboardUrl(currentUser);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-start py-8 px-4">
      <div className="mb-6">
        <img src={logoImage} alt="Learnovate360" className="h-28.75 w-61 object-contain" />
      </div>

      <div className="animate-fade-in-down bg-white rounded-lg shadow-elevation-1 w-full max-w-186.75 px-11.75 pt-6 pb-8 mb-8">
        <Heading as="h2" className="text-[22px] font-medium text-black leading-7 mb-6 text-center">
          Complete Profile
        </Heading>

        <CollapsibleContainer isOpen={!email} className="mb-6">
          {!email && <Alert variant="destructive">{MISSING_ONBOARDING_EMAIL}</Alert>}
        </CollapsibleContainer>

        <CollapsibleContainer isOpen={!!error} className="mb-6">
          <Alert variant="destructive" onDismiss={() => setError(null)}>
            {error || ''}
          </Alert>
        </CollapsibleContainer>

        <div className="mb-2">
          <Paragraph className="text-lg font-medium text-black leading-6 mb-6">
            Company Logo
          </Paragraph>
          <div className="flex items-center gap-5">
            <Avatar className="w-25 h-25 bg-brand-primary overflow-hidden">
              {logoPreviewUrl ? (
                <img
                  src={logoPreviewUrl}
                  alt="Company logo preview"
                  className="w-full h-full object-cover object-center"
                />
              ) : (
                <AvatarFallback className="bg-brand-primary text-white text-[40px] font-medium leading-11 tracking-[-0.8px]">
                  {orgName.slice(0, 2).toUpperCase() || 'CA'}
                </AvatarFallback>
              )}
            </Avatar>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              id="logo-upload"
            />
            <Button
              type="button"
              variant="auth"
              onClick={() => fileInputRef.current?.click()}
              className="h-9 w-27 px-3 py-2 rounded text-sm font-medium leading-5"
            >
              Upload
            </Button>
            {logoFile && (
              <Paragraph className="text-xs text-gray-placeholder">{logoFile.name}</Paragraph>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <FormInputField
              id="orgName"
              label="Organisation Name"
              value={orgName}
              onChange={(e) => {
                setOrgName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, orgName: undefined }));
              }}
              placeholder="Organisation Name"
              disabled={isFormDisabled}
              error={fieldErrors.orgName}
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />
            <FormInputField
              id="company-email"
              type="email"
              label="Email Address"
              value={email}
              readOnly
              placeholder="Email Address"
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName="h-11 rounded px-4 py-2 text-sm text-black bg-gray-50 cursor-not-allowed focus-visible:ring-offset-0 border-gray-placeholder"
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormInputField
              id="phone"
              type="tel"
              label="Phone Number"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setFieldErrors((prev) => ({ ...prev, phone: undefined }));
              }}
              placeholder="Phone Number"
              disabled={isFormDisabled}
              error={fieldErrors.phone}
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />
            <FormInputField
              id="adminName"
              label="Admin Name"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="Admin Name"
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormSelectField
              id="roleInCompany"
              label="Role in company"
              value={roleInCompany}
              onValueChange={setRoleInCompany}
              options={ROLE_OPTIONS}
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
              placeholder="Role in company"
            />
            <FormInputField
              id="companyReg"
              label="Company Registration (optional)"
              value={companyReg}
              onChange={(e) => setCompanyReg(e.target.value)}
              placeholder="Company Registration (optional)"
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormInputField
              id="ofstedNumber"
              label="Ofsted Register Number (optional)"
              value={ofstedNumber}
              onChange={(e) => setOfstedNumber(e.target.value)}
              placeholder="Ofsted Register Number (optional)"
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />
            <FormInputField
              id="ofstedLink"
              label="Ofsted Report Link (optional)"
              value={ofstedLink}
              onChange={(e) => {
                setOfstedLink(e.target.value);
                setFieldErrors((prev) => ({ ...prev, ofstedLink: undefined }));
              }}
              placeholder="https://..."
              disabled={isFormDisabled}
              error={fieldErrors.ofstedLink}
              labelClassName={FORM_LABEL_CLASS_NAME}
              inputClassName={FORM_INPUT_CLASS_NAME}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormSelectField
              id="typeOfProvision"
              label="Type of Provision"
              value={typeOfProvision}
              onValueChange={setTypeOfProvision}
              options={TYPE_OF_PROVISION_OPTIONS}
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
              placeholder="Type of Provision"
            />
            <FormSelectField
              id="localAuthority"
              label="Local Authority (optional)"
              value={localAuthority}
              onValueChange={setLocalAuthority}
              options={LOCAL_AUTHORITY_OPTIONS}
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
              placeholder="Local Authority (optional)"
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormSelectField
              id="country"
              label="Country"
              value={country}
              onValueChange={setCountry}
              options={COUNTRY_OPTIONS}
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
              placeholder="Country"
            />
            <FormSelectField
              id="region"
              label="Region"
              value={region}
              onValueChange={setRegion}
              options={REGION_OPTIONS}
              disabled={isFormDisabled}
              labelClassName={FORM_LABEL_CLASS_NAME}
              triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
              placeholder="Region"
            />
          </div>

          <FormTextareaField
            id="statementOfPurpose"
            label="Statement of purpose"
            value={statementOfPurpose}
            onChange={(e) => setStatementOfPurpose(e.target.value)}
            placeholder="Statement of purpose"
            disabled={isFormDisabled}
            labelClassName={FORM_LABEL_CLASS_NAME}
            textareaClassName={`min-h-11 h-11 ${FORM_TEXTAREA_CLASS_NAME}`}
          />

          <FormTextareaField
            id="additionalInfo"
            label="Additional information"
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder="Additional information"
            disabled={isFormDisabled}
            labelClassName={FORM_LABEL_CLASS_NAME}
            textareaClassName={`min-h-25 h-25 ${FORM_TEXTAREA_CLASS_NAME}`}
          />

          <div className="grid grid-cols-2 gap-2.5 mt-3.5">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              disabled={isFormDisabled}
              className="h-11 rounded px-4 py-2.5 text-lg font-medium leading-6 text-brand-primary bg-brand-tint hover:bg-brand-tint/80"
            >
              Skip for now
            </Button>
            <Button
              type="submit"
              variant="auth"
              disabled={isFormDisabled}
              className="h-11 rounded px-4 py-2.5 text-lg font-medium leading-6"
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
